import I18n from '@/modules/i18n';
import { lmsProviderConfig } from '@/components/shared/Lms/lmsProviders';
import { LmsIconKind } from '@/components/shared/Lms/types';

export interface LmsIconConfig {
  alt: string;
  logo: string;
  tooltip: {
    body: string;
    header: string | null;
    link: string | null;
    linkText: string;
  };
}

interface LmsIconParams {
  hasError: boolean;
  isGoogleDraft?: boolean;
  isOrphaned: boolean;
  kind: LmsIconKind;
}

// Error wins over pending; a connected assignment with neither renders the plain success logo elsewhere.
export const getLmsIconConfig = ({
  hasError, isGoogleDraft = false, isOrphaned, kind,
}: LmsIconParams): LmsIconConfig | null => {
  const { icons, name, urls } = lmsProviderConfig(kind);

  if (hasError) {
    return {
      alt: `Error with ${name}`,
      logo: icons.failed,
      tooltip: {
        body: I18n.t('grade_sync_error_message', { name }),
        header: null,
        link: urls.troubleshoot ?? null,
        linkText: I18n.t('grade_sync_troubleshooting_steps'),
      },
    };
  }

  if (isGoogleDraft && kind === 'google' && icons.orphaned) {
    return {
      alt: `${name} assignment is a draft`,
      logo: icons.orphaned,
      tooltip: {
        body: I18n.t('grade_sync_google_draft_body'),
        header: I18n.t('grade_sync_google_draft_header'),
        link: null,
        linkText: '',
      },
    };
  }

  if (isOrphaned && kind !== 'google' && icons.orphaned) {
    return {
      alt: `${name} assignment not connected`,
      logo: icons.orphaned,
      tooltip: {
        body: I18n.t('grade_sync_orphaned_body', { name }),
        header: I18n.t('grade_sync_orphaned_header', { name }),
        link: urls.unlinkedAssignment ?? null,
        linkText: I18n.t('learn_more'),
      },
    };
  }

  return null;
};

export default getLmsIconConfig;
